'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr/immutable'
import { isPassengerDataFormHidden } from '@/root/modules/shared/SelectTicketPage/helpers/passengerDataForm/passengerDataForm'
import {
  getResidentOverrideCategory,
  type ResidentOverrideCategory,
} from '@/root/modules/shared/SelectTicketPage/helpers/residentOverride/residentOverride'
import { Price, PriceResponse } from '@/root/shared-types/dto/SailPricing'
import { PassengerFormData } from '@/root/shared-types/selectTicketPage/selectTicket'
import type { Key } from '@/root/shared-hooks/useGetTranslations/useGetTranslations'
import { Locale } from '@/root/shared-types'
import { ReservationWithModification, type TicketQuantityChanges } from '@/root/modules/shared/EditReservation/types'
import {
  aggregateResidentPassengers,
  aggregateResidentQuantities,
  areQuantitiesEqual,
  buildItemModifications,
  getResidentBucketCode,
  getResidentSubCategoryCodes,
  groupQuantitiesByPriceCategory,
  handleStaleServerAction,
  isCompanyLoyaltyEnabled,
  isHandicappedVehiclesEnabled,
  showWarningToast,
} from '@/root/modules/shared/EditReservation/utils'
import { isTicketItem } from '@/root/shared-helpers/reservation/reservation-item-filters'
import { modify } from '@/app/actions/reservation-modification'
import { useAddResidentFlow } from '@/root/modules/shared/EditReservation/hooks/useAddResidentFlow'
import { MODIFY_ERROR_MESSAGE_KEYS } from '@/root/modules/shared/EditReservation/consts'

const isVehiclePrice = (price: Price): boolean => price.priceCategory.type === 'CAR_DECK'

export interface UseTicketEditTabStateParams {
  getTranslation: (key: Key) => string
  reservation: ReservationWithModification
  sailPackageSeqN: number
  sailRefId?: number
  sailPackageCode: string
  sailRefIds: number[]
  reservationToken: string
  locale: Locale
  disabled?: boolean
  onTicketSaved: (updated: ReservationWithModification) => void
  onPendingChange?: (pending: boolean) => void
  onUnsavedFormsChange?: (hasUnsaved: boolean) => void
  onError?: (message: string, code?: string) => void
  onErrorClear?: () => void
}

export interface UseTicketEditTabStateReturn {
  prices: PriceResponse | undefined
  isLoading: boolean
  error: unknown
  isDisabled: boolean
  hidePassengerDataForm: boolean
  handicappedVehiclesEnabled: boolean
  companyLoyaltyEnabled: boolean
  routeAttributes?: Record<string, boolean | string>
  currentRoute?: string
  currentDepartureDate?: string
  initialQuantityByCode: Record<string, number>
  initialPassengersByCode: Record<string, PassengerFormData[]>
  displayInitialQuantityByCode: Record<string, number>
  displayInitialPassengersByCode: Record<string, PassengerFormData[]>
  adjustQuantity: (code: string, delta: number, deleteSeqN?: number) => Promise<void>
  setQuantity: (price: Price, qty: number) => Promise<void>
  handlePassengerDeleted: (
    price: Price,
    slotIndex: number,
    verifiedPriceCategory?: string,
    seqN?: number
  ) => Promise<void>
  handleResidentIdVerified: (
    price: Price,
    slotIndex: number,
    personalId: string,
    priceCategory: string
  ) => Promise<number | null>
  residentOverride?: ResidentOverrideCategory
  toggleResidentOverride: ReturnType<typeof useAddResidentFlow>['toggleResidentOverride']
  handleFormStateChange: (priceCode: string, saved: number, total: number) => void
  handleCancelledOverlayClick: () => void
}

export const useTicketEditTabState = ({
  getTranslation,
  reservation,
  sailPackageSeqN,
  sailRefId,
  sailPackageCode,
  sailRefIds,
  reservationToken,
  locale,
  disabled = false,
  onTicketSaved,
  onPendingChange,
  onUnsavedFormsChange,
  onError,
  onErrorClear,
}: UseTicketEditTabStateParams): UseTicketEditTabStateReturn => {
  const { data: prices, isLoading, error } = useSWR<PriceResponse>(sailRefId ? `/api/prices/${sailRefId}` : null)

  const currentItems = useMemo(
    () => (reservation.items ?? []).filter((item) => isTicketItem(item) && item.sailPackageSeqN === sailPackageSeqN),
    [reservation.items, sailPackageSeqN]
  )

  const initialQuantityByCode = useMemo(() => groupQuantitiesByPriceCategory(currentItems), [currentItems])
  const lastInitialQuantityByCodeRef = useRef<Record<string, number>>(initialQuantityByCode)

  const initialPassengersByCode = useMemo((): Record<string, PassengerFormData[]> => {
    const guestsBySeqN = new Map((reservation.guests ?? []).map((g) => [g.seqN, g]))
    const result: Record<string, PassengerFormData[]> = {}

    for (const item of currentItems) {
      if (item.priceCategorySubType !== 'DECK') continue
      result[item.priceCategory] = (item.ownerSeqNs ?? []).map((seqN) => {
        const guest = guestsBySeqN.get(seqN)
        return {
          firstName: guest?.firstName ?? '',
          lastName: guest?.lastName ?? '',
          birthdate: guest?.birthday ?? '',
          citizenship: guest?.citizenship ?? '',
          personalId: guest?.personalIdentificationNumber ?? '',
          gender: guest?.gender,
          priceCategory: item.priceCategory,
          additionalInfo: guest?.comments ?? '',
          personalIdConfirmed: item.resident,
          seqN,
        }
      })
    }

    return result
  }, [currentItems, reservation.guests])

  const residentBucketCode = useMemo(() => getResidentBucketCode(prices?.prices), [prices])
  const residentSubCategoryCodes = useMemo(() => getResidentSubCategoryCodes(currentItems), [currentItems])
  const displayInitialQuantityByCode = useMemo(
    () => aggregateResidentQuantities(initialQuantityByCode, residentSubCategoryCodes, residentBucketCode),
    [initialQuantityByCode, residentSubCategoryCodes, residentBucketCode]
  )
  const displayInitialPassengersByCode = useMemo(
    () => aggregateResidentPassengers(initialPassengersByCode, residentSubCategoryCodes, residentBucketCode),
    [initialPassengersByCode, residentSubCategoryCodes, residentBucketCode]
  )

  const [quantityByCode, setQuantityByCode] = useState<Record<string, number>>(initialQuantityByCode)

  useEffect(() => {
    if (areQuantitiesEqual(lastInitialQuantityByCodeRef.current, initialQuantityByCode)) {
      return
    }
    lastInitialQuantityByCodeRef.current = initialQuantityByCode
    setQuantityByCode(initialQuantityByCode)
  }, [initialQuantityByCode])

  const allPrices = useMemo(() => [...(prices?.prices ?? []), ...(prices?.carDeckPrices ?? [])], [prices])
  const byCode = useMemo(() => new Map(allPrices.map((price) => [price.priceCategory.code, price])), [allPrices])

  const isPendingRef = useRef(false)
  const [isPending, setIsPending] = useState(false)

  const [formStateByCodes, setFormStateByCodes] = useState<Record<string, { saved: number; total: number }>>({})

  const handleFormStateChange = useCallback((priceCode: string, saved: number, total: number) => {
    setFormStateByCodes((prev) => ({ ...prev, [priceCode]: { saved, total } }))
  }, [])

  useEffect(() => {
    const hasUnsaved = Object.values(formStateByCodes).some(({ saved, total }) => saved < total)
    onUnsavedFormsChange?.(hasUnsaved)
  }, [formStateByCodes, onUnsavedFormsChange])

  useEffect(() => {
    onPendingChange?.(isPending)
  }, [isPending, onPendingChange])

  const applyQuantityChange = useCallback(
    async (newQuantityByCode: Record<string, number>, deleteSeqN?: number) => {
      if (isPendingRef.current) return
      if (!sailPackageCode || sailRefIds.length === 0) return
      onErrorClear?.()

      const keys = new Set([...Object.keys(initialQuantityByCode), ...Object.keys(newQuantityByCode)])
      const changes: TicketQuantityChanges = { passengers: {}, vehicles: {} }
      keys.forEach((code) => {
        const currentQty = initialQuantityByCode[code] ?? 0
        const newQty = newQuantityByCode[code] ?? 0
        if (currentQty === newQty) return
        const price = byCode.get(code)
        if (price && isVehiclePrice(price)) {
          changes.vehicles![code] = newQty
        } else {
          changes.passengers![code] = newQty
        }
      })

      const hasChanges =
        Object.keys(changes.passengers ?? {}).length > 0 || Object.keys(changes.vehicles ?? {}).length > 0
      if (!hasChanges) return

      // One tab edits exactly one sail package: a round trip is two independent tabs, so a passenger
      // added here belongs to this leg only.
      const modifications = buildItemModifications({
        reservation,
        sailPackageSeqN,
        sailPackage: sailPackageCode,
        sailRefIds,
        newQuantities: changes,
        deleteSeqN,
      })
      if (modifications.length === 0) return

      isPendingRef.current = true
      setIsPending(true)
      const prevQuantityByCode = quantityByCode
      setQuantityByCode(newQuantityByCode)

      let modifyResult: Awaited<ReturnType<typeof modify>>
      try {
        modifyResult = await modify(reservationToken, modifications, locale)
      } catch (err) {
        isPendingRef.current = false
        setIsPending(false)

        if (
          handleStaleServerAction({
            error: err,
            getTranslation,
            onError: (message) => onError?.(message),
          })
        ) {
          return
        }

        throw err
      }

      isPendingRef.current = false
      setIsPending(false)

      if (!modifyResult.success) {
        setQuantityByCode(prevQuantityByCode)
        const messageKey =
          (modifyResult.error.code && MODIFY_ERROR_MESSAGE_KEYS[modifyResult.error.code]) ||
          'commonNotificationErrorDesc'
        onError?.(getTranslation(messageKey), modifyResult.error.code)
        throw new Error(modifyResult.error.message ?? 'modify failed')
      }

      onTicketSaved(modifyResult.data)

      setFormStateByCodes((prev) => {
        const updated = { ...prev }
        for (const code of Object.keys(updated)) {
          if ((newQuantityByCode[code] ?? 0) === 0) {
            delete updated[code]
          }
        }
        return updated
      })
    },
    [
      quantityByCode,
      initialQuantityByCode,
      byCode,
      sailPackageSeqN,
      sailPackageCode,
      sailRefIds,
      reservation,
      reservationToken,
      locale,
      getTranslation,
      onTicketSaved,
      onErrorClear,
      onError,
    ]
  )

  const adjustQuantity = useCallback(
    (code: string, delta: number, deleteSeqN?: number) => {
      return applyQuantityChange(
        { ...quantityByCode, [code]: Math.max(0, (quantityByCode[code] ?? 0) + delta) },
        deleteSeqN
      )
    },
    [applyQuantityChange, quantityByCode]
  )

  const setQuantity = useCallback(
    async (price: Price, qty: number): Promise<void> => {
      await applyQuantityChange({ ...quantityByCode, [price.priceCategory.code]: Math.max(0, qty) })
    },
    [applyQuantityChange, quantityByCode]
  )

  const handlePassengerDeleted = useCallback(
    async (price: Price, _slotIndex: number, verifiedPriceCategory?: string, seqN?: number) => {
      // All resident tickets share one bucket card, whose price is the first resident category.
      // A slot without a guest seqN holds no ticket yet — the ID check is what adds it — so
      // charging its removal to the bucket would delete a stranger's resident ticket.
      if (price.priceCategory.isResident && !seqN) return

      // The card the user closed belongs to one guest, so the removal names them. Without a seqN —
      // a slot added in this session, not yet reloaded from the booking — the last one added goes.
      const targetCode = verifiedPriceCategory ?? price.priceCategory.code
      await adjustQuantity(targetCode, -1, seqN)
    },
    [adjustQuantity]
  )

  const {
    route: currentRoute,
    departureDate: currentDepartureDate,
    handleResidentIdVerified,
    toggleResidentOverride,
  } = useAddResidentFlow({
    currentReservation: reservation,
    reservationToken,
    locale,
    sailPackageSeqN,
    sailPackageCode,
    sailRefId,
    sailRefIds,
    isPendingRef,
    setIsPending,
    getTranslation,
    onReservationUpdated: onTicketSaved,
    onError,
    onErrorClear,
  })

  const handleCancelledOverlayClick = useCallback(() => {
    showWarningToast(getTranslation('editSailCancelledTicketsDisabled'))
  }, [getTranslation])

  const sailPackage = reservation.sailPackages?.find((pkg) => pkg.seqN === sailPackageSeqN)

  const residentOverride = getResidentOverrideCategory(prices?.prices, locale, sailPackage)
  const hidePassengerDataForm = isPassengerDataFormHidden(sailPackage)
  const handicappedVehiclesEnabled = isHandicappedVehiclesEnabled(sailPackage)
  const companyLoyaltyEnabled = isCompanyLoyaltyEnabled(sailPackage)
  const isDisabled = disabled || isPending

  return {
    prices,
    isLoading,
    error,
    isDisabled,
    hidePassengerDataForm,
    handicappedVehiclesEnabled,
    companyLoyaltyEnabled,
    routeAttributes: sailPackage?.routeAttributes,
    currentRoute,
    currentDepartureDate,
    initialQuantityByCode,
    initialPassengersByCode,
    displayInitialQuantityByCode,
    displayInitialPassengersByCode,
    adjustQuantity,
    setQuantity,
    handlePassengerDeleted,
    handleResidentIdVerified,
    residentOverride,
    toggleResidentOverride,
    handleFormStateChange,
    handleCancelledOverlayClick,
  }
}
